import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { VillageReferenceRecord, VillageViewModel } from "../../../domain/contracts";
import type { RouteSelectionController } from "../../../hooks/useRouteSelection";
import { mapVillagesQueryOptions, villageReferenceQueryOptions } from "../../../services/queries/publicQueryOptions";
import { buildMapVillagePoints, buildPreviewVillageViewModels } from "../../../services/mapPointModels";

type MapControllerResult = {
  status: {
    isLoading: boolean;
    isError: boolean;
    errorMessage: string | null;
  };
  reference: VillageReferenceRecord[];
  mapVillages: VillageViewModel[];
  previewVillages: VillageViewModel[];
  selectedVillageRef: string | null;
  selectedVillageRecord: VillageViewModel | null;
  selectedVillageBackendRef: string | null;
  handlers: {
    selectVillage: (villageRef: string | null) => void;
  };
};

export function useMapController(routeSelection: RouteSelectionController): MapControllerResult {
  const villageReferenceQuery = useQuery(villageReferenceQueryOptions());
  const mapVillagesQuery = useQuery(mapVillagesQueryOptions());

  const reference = villageReferenceQuery.data ?? [];
  const previewVillageRecords = useMemo(() => {
    const villages = mapVillagesQuery.data?.items ?? [];
    return buildPreviewVillageViewModels(villages, reference);
  }, [mapVillagesQuery.data?.items, reference]);

  const mapVillages = useMemo(() => {
    const villages = mapVillagesQuery.data?.items ?? [];
    return buildMapVillagePoints(villages, reference);
  }, [mapVillagesQuery.data?.items, reference]);

  const previewVillages = useMemo(() => {
    return previewVillageRecords.filter((village) => village.stats.interviewCount > 0);
  }, [previewVillageRecords]);

  useEffect(() => {
    if (!routeSelection.villageRef || mapVillages.length === 0) {
      return;
    }

    const hasMatchingVillage = mapVillages.some(
      (item) => item.id === routeSelection.villageRef || item.slug === routeSelection.villageRef,
    );

    if (!hasMatchingVillage) {
      routeSelection.setVillageRef(null);
      // If the village doesn't exist, also clear the view so we don't show a blank village page
      if (routeSelection.viewRef === "village" || routeSelection.viewRef === "interviewee") {
        routeSelection.setViewRef("map");
      }
    }
  }, [mapVillages, routeSelection]);

  const selectedVillageRef = routeSelection.villageRef;
  const selectedVillageRecord = useMemo(
    () => {
      if (!selectedVillageRef) {
        return null;
      }

      return mapVillages.find((item) => item.id === selectedVillageRef || item.slug === selectedVillageRef) ?? null;
    },
    [mapVillages, selectedVillageRef],
  );

  return {
    status: {
      isLoading: mapVillagesQuery.isLoading || villageReferenceQuery.isLoading,
      isError: mapVillagesQuery.isError || villageReferenceQuery.isError,
      errorMessage: mapVillagesQuery.error instanceof Error
        ? mapVillagesQuery.error.message
        : villageReferenceQuery.error instanceof Error
          ? villageReferenceQuery.error.message
          : null,
    },
    reference,
    mapVillages,
    previewVillages,
    selectedVillageRef,
    selectedVillageRecord,
    selectedVillageBackendRef: selectedVillageRecord?.backendRef ?? null,
    handlers: {
      selectVillage(villageRef: string | null) {
        routeSelection.setVillageRef(villageRef);
      },
    },
  };
}